"use client";

import { useCallback, type ReactNode } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useLocale } from "next-intl";
import * as Sentry from "@sentry/nextjs";

export const GOOGLE_MAPS_MAP_ID =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID ?? undefined;

interface MapsApiProviderProps {
  children: ReactNode;
}

// Forwards Google Maps JS API load failures (e.g. mobile Safari sub-module
// timeouts that previously surfaced as unhandled `Could not load "util"`
// rejections) to Sentry under tags.library=google-maps with `warning` level.
// The `before-send` filter additionally re-tags pure external-script frames
// to `info` so the two paths converge on a single dashboard segment.
export function MapsApiProvider({ children }: MapsApiProviderProps) {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;
  const locale = useLocale();

  const handleError = useCallback((error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        source: "external_script",
        library: "google-maps",
        boundary: "api-provider",
      },
      level: "warning",
    });
  }, []);

  if (!apiKey) {
    return <>{children}</>;
  }

  return (
    <APIProvider apiKey={apiKey} language={locale} onError={handleError}>
      {children}
    </APIProvider>
  );
}
